import { ArrowCounterClockwise, UsersThree } from "@phosphor-icons/react";
import { useEffect, useReducer } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { DEFAULT_LEADS_SEARCH_PARAMS } from "common/constants";
import useLeadsInfo from "common/datahooks/useLeadsInfo";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { formatNumber } from "common/helpers/utils";
import useIntersection from "common/hooks/useIntersection";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

import useDripLeads from "../../../../datahooks/useDripLeads";
import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import { parseDripFilters } from "../../../../utils";
import ProfileCard from "../../../ProfileCard";
import SectionHeading from "../../../SectionHeading";
import StepperNextButton from "../../../StepperNextButton";
import Stepper from "../../Stepper";
import DesktopFilters from "./Filters/DesktopFilters";
import MobileFilters from "./Filters/MobileFilters";
import KeywordsSearch from "./KeywordsSearch";

import type { DripTargeting } from "../../../../types";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

const PAGE_SIZE = 24;

export default function Drip({
  initialFilters,
  openLeadPreview,
}: {
  initialFilters: DripTargeting | undefined;
  openLeadPreview: (leadId: string) => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { ref, isIntersecting } = useIntersection();
  const { owners } = useCampaignContext();
  const { isSettingTargeting, setTargeting } = useTargetingMutations();

  const [filters, setFilters] = useReducer(
    (
      prevState: LeadsSearchParams<SelectedFacetParam>,
      newState: Partial<LeadsSearchParams<SelectedFacetParam>>,
    ) => ({
      ...prevState,
      ...newState,
    }),
    {
      ...DEFAULT_LEADS_SEARCH_PARAMS,
      lead_of: [
        {
          id: owners[0],
          name: owners[0],
        },
      ],
    },
  );

  const { resetAllParams, resetAllFilters, hasFilters } = useLeadsSearchParams(
    filters,
    setFilters,
  );

  const resetFiltersButton = hasFilters && (
    <Button
      leftIcon={<ArrowCounterClockwise />}
      variant="secondary-black"
      size="sm"
      onClick={resetAllFilters}
    >
      Reset all
    </Button>
  );

  const { leadsInfo, isFetchingLeadsInfo } = useLeadsInfo(filters);
  const {
    leads,
    isFetchingMoreLeads,
    hasMoreDripLeads,
    isDripLeadsError,
    isFetchingLeads,
    refetchDripLeads,
    fetchMoreLeads,
  } = useDripLeads({
    pageSize: PAGE_SIZE,
    searchParams: filters,
  });

  useEffect(() => {
    if (isIntersecting && hasMoreDripLeads && !isFetchingMoreLeads) {
      fetchMoreLeads();
    }
  }, [hasMoreDripLeads, isFetchingMoreLeads, isIntersecting, fetchMoreLeads]);

  useEffect(() => {
    if (initialFilters) {
      const locationMap = new Map<string, string>();
      initialFilters.locations.forEach((location) => {
        if (locationMap.has(location.name)) {
          locationMap.set(
            location.name,
            `${locationMap.get(location.name)},${location.id}`,
          );
        } else {
          locationMap.set(location.name, location.id);
        }
      });
      setFilters({
        ...initialFilters,
        locations: Array.from(locationMap).map(([name, id]) => ({
          name,
          id,
        })),
      });
    }
  }, []);

  const isLoading = isFetchingLeads && !isFetchingMoreLeads;

  const totalLeadsTag = isFetchingLeadsInfo ? (
    <Skeleton className="h-7 w-16 max-lg:mb-4 max-lg:ml-auto" />
  ) : (
    <Tag
      className=" max-lg:mb-4 max-lg:ml-auto"
      size="lg"
      variant="secondary"
      leftIcon={<UsersThree weight="fill" />}
    >
      {formatNumber(leadsInfo.totalLeads)}
    </Tag>
  );

  const searchComponent = (
    <KeywordsSearch
      keywords={filters.keywords}
      setKeywords={(keywords) => setFilters({ keywords })}
    />
  );

  return (
    <>
      <SectionHeading
        title="Targeting Setup"
        subtitle="Select your campaign targets using filters and a search keyword"
      />

      {isDripLeadsError ? (
        <ErrorPlaceholder
          errorMessage="Error loading leads"
          onRetry={refetchDripLeads}
        />
      ) : (
        <>
          <RenderIf condition={isDesktop}>
            <div className="flex items-center justify-between gap-4 border-b border-black-200 pb-3">
              {searchComponent}

              {totalLeadsTag}
            </div>
          </RenderIf>

          {isDesktop ? (
            <DesktopFilters
              resetFiltersButton={resetFiltersButton}
              searchParams={filters}
              setSearchParams={setFilters}
            />
          ) : (
            <MobileFilters
              searchParams={filters}
              setSearchParams={setFilters}
            />
          )}

          <RenderIf condition={!isDesktop}>
            {searchComponent}
            {totalLeadsTag}
          </RenderIf>

          {isLoading || (leads && leads.length) ? (
            <>
              <section className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:grid-cols-3">
                {(isLoading ? new Array(PAGE_SIZE).fill({}) : leads).map(
                  (lead, index) => (
                    <ProfileCard
                      key={lead ? lead.id : index}
                      profile={lead}
                      isLoading={isLoading}
                      openPreview={() => {
                        openLeadPreview(lead.id);
                      }}
                    />
                  ),
                )}

                <RenderIf condition={isFetchingMoreLeads}>
                  {new Array(PAGE_SIZE).fill({}).map((_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
                    <ProfileCard key={`new-profile-${index}`} isLoading />
                  ))}
                </RenderIf>
              </section>

              <div ref={ref} className="opacity-0">
                Load more content
              </div>
            </>
          ) : (
            <EmptyPlaceholder
              imageSrc={NoResultsImage}
              title="No results found"
              subtitle="There were no results found for your search. Try searching for something else"
              actionComponent={
                <Button
                  leftIcon={<ArrowCounterClockwise />}
                  variant="secondary-black"
                  onClick={resetAllParams}
                >
                  Clear search
                </Button>
              }
            />
          )}
        </>
      )}

      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            onClick={() =>
              setTargeting({ campaignTargeting: parseDripFilters(filters) })
            }
            isLoading={isSettingTargeting}
            disabled={!leads || !leads.length}
          />
        }
      />
    </>
  );
}
